import React, { useState, useEffect, useContext } from 'react';
import { BottomNavigation, BottomNavigationAction, Paper, Box } from '@mui/material';
import { Dashboard, Map, Lightbulb, Build, AutoFixHigh } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import './BottomNav.scss';
import { AuthContext } from '../../hooks/AuthProvider';
import { useTheme } from '../../hooks/ThemeProvider';
import { motion, AnimatePresence } from 'framer-motion';
import { NavigationAnalytics } from '../../lib/analytics';

/**
 * A functional React component that renders a bottom navigation bar.
 * It uses React Router for navigation and responds to authentication state changes.
 */
const BottomNav: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [value, setValue] = useState(0);
  const authContext = useContext(AuthContext);
  const [visible, setVisible] = useState(authContext.isAuthenticated);
  const { isDarkMode: _ } = useTheme();

  useEffect(() => {
    const pathToIndex: { [key: string]: number } = {
      '/': 0,
      '/map': 1,
      '/builder': 2,
      '/find': 3,
      // '/inspiration': 4,
    };
    setValue(pathToIndex[location.pathname] || 0);
  }, [location]);

  useEffect(() => {
    setVisible(authContext.isAuthenticated);
  }, [authContext]);

  // Force clear any pending gestures and interactions when navigation is triggered
  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    // Create a synthetic event to force any active touch handlers to clean up
    if (typeof document !== 'undefined') {
      // Force touch end/cancel events to propagate
      document.dispatchEvent(new Event('touchcancel', { bubbles: true }));
      document.dispatchEvent(new Event('mouseup', { bubbles: true }));
    }

    // Artificially delay to ensure events propagate
    setTimeout(() => {
      // const indexToPath = ['/', '/map', '/builder', '/inspiration', '/find'];
      const indexToPath = ['/', '/map', '/builder', '/find'];
      const targetPath = indexToPath[newValue];
      
      // Track bottom navigation clicks
      NavigationAnalytics.bottomNav(targetPath);
      
      navigate(targetPath);
    }, 0);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 1201 }}
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        >
          <Paper className="bottom-nav glassmorphic-bottom-nav" elevation={3}>
            <BottomNavigation
              value={value}
              onChange={handleChange}
              showLabels
              className="nav-content"
            >
              <BottomNavigationAction
                label="Dashboard"
                data-testid="dashboard-nav-action"
                icon={<Dashboard />}
                className="nav-action"
              />
              <BottomNavigationAction
                label="Map"
                data-testid="map-nav-action"
                icon={<Map />}
                className="nav-action"
              />
              <BottomNavigationAction
                label="Builder"
                data-testid="plan-builder-nav-action"
                icon={<Build />}
                className="nav-action"
              />
              <BottomNavigationAction
                label="Find"
                data-testid="ai-planner-nav-action"
                icon={<AutoFixHigh />}
                className="nav-action"
              />
              {/* <BottomNavigationAction
                label="Inspiration"
                data-testid="inspiration-nav-action"
                icon={<Lightbulb />}
                className="nav-action"
              />
              */}
            </BottomNavigation>
          </Paper>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default BottomNav;
